import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  User as UserIcon,
  CreditCard,
  Receipt,
  AlertCircle,
  Loader2,
  ExternalLink,
  Check,
  X,
  Pencil,
  ShieldAlert,
  Trash2,
  Mail,
  Bell,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Toggle } from '../components/Toggle';
import {
  getMe,
  updateMyProfile,
  getMySubscription,
  getMyInvoices,
  createMyBillingPortalSession,
  cancelMySubscription,
  deleteMyAccount,
  sendVerificationEmail,
  getNotificationPreferences,
  updateNotificationPreferences,
  type MySubscription,
  type MyInvoice,
  type NotificationPreferences,
} from '../api/client';

// ---------------------------------------------------------------------------
// Stripe currency / status helpers
// ---------------------------------------------------------------------------

const formatMoney = (amount: number | null | undefined, currency: string) => {
  if (amount === null || amount === undefined) return '—';
  // Stripe amounts are in the smallest currency unit (cents)
  const value = amount / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency.toUpperCase()}`;
  }
};

const formatUnixDate = (ts: number | null | undefined) => {
  if (!ts) return '—';
  return new Date(ts * 1000).toLocaleDateString('en-AU');
};

const subscriptionStatusLabel = (sub: MySubscription): string => {
  if (!sub.has_subscription) return 'No active subscription';
  if (sub.cancel_at_period_end) return 'Cancels at period end';
  return (sub.status || '').replace(/_/g, ' ') || 'Unknown';
};

const subscriptionStatusTone = (sub: MySubscription): string => {
  const status = sub.status;
  if (sub.cancel_at_period_end) {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
  }
  if (status === 'active' || status === 'trialing') {
    return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
  }
  if (status === 'past_due' || status === 'unpaid') {
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  }
  return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function Account() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();

  // Profile state
  const [emailDraft, setEmailDraft] = useState(user?.email || '');
  const [editingEmail, setEditingEmail] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);

  // Subscription / invoices
  const [subscription, setSubscription] = useState<MySubscription | null>(null);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [invoices, setInvoices] = useState<MyInvoice[]>([]);
  const [invoicesError, setInvoicesError] = useState<string | null>(null);
  const [invoicesLoading, setInvoicesLoading] = useState(true);

  // Billing portal / cancel state
  const [openingPortal, setOpeningPortal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelImmediately, setCancelImmediately] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // Verification + deletion state
  const [sendingVerify, setSendingVerify] = useState(false);
  const [verifySent, setVerifySent] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Notification preferences
  const [notifPrefs, setNotifPrefs] = useState<NotificationPreferences | null>(null);
  const [notifLoading, setNotifLoading] = useState(true);
  const [notifError, setNotifError] = useState<string | null>(null);
  // In-flight kind so we can disable just that toggle while saving
  // rather than locking the whole list.
  const [notifSaving, setNotifSaving] = useState<string | null>(null);

  const loadNotifPrefs = async () => {
    setNotifLoading(true);
    try {
      const p = await getNotificationPreferences();
      setNotifPrefs(p);
      setNotifError(null);
    } catch {
      setNotifError('Failed to load notification preferences.');
    } finally {
      setNotifLoading(false);
    }
  };

  const onToggleNotif = async (kind: string, enabled: boolean) => {
    if (!notifPrefs) return;
    // Optimistic so the toggle feels instant. Revert on failure.
    const previous = notifPrefs;
    setNotifPrefs({
      ...notifPrefs,
      preferences: { ...notifPrefs.preferences, [kind]: enabled },
    });
    setNotifSaving(kind);
    try {
      const next = await updateNotificationPreferences({ [kind]: enabled });
      setNotifPrefs(next);
    } catch {
      setNotifPrefs(previous);
      setNotifError('Failed to update preference. Please try again.');
    } finally {
      setNotifSaving(null);
    }
  };

  useEffect(() => {
    setEmailDraft(user?.email || '');
  }, [user?.email]);

  const loadBilling = async () => {
    setSubscriptionLoading(true);
    setInvoicesLoading(true);
    try {
      const sub = await getMySubscription();
      setSubscription(sub);
      setSubscriptionError(null);
    } catch (err) {
      const detail =
        (err as { response?: { status?: number; data?: { detail?: string } } })?.response?.data
          ?.detail;
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setSubscription({ has_subscription: false });
        setSubscriptionError(null);
      } else if (status === 503) {
        setSubscriptionError('Billing is not configured on this server.');
      } else {
        setSubscriptionError(detail || 'Failed to load subscription');
      }
    } finally {
      setSubscriptionLoading(false);
    }

    try {
      const list = await getMyInvoices(12);
      setInvoices(list);
      setInvoicesError(null);
    } catch (err) {
      const detail =
        (err as { response?: { status?: number; data?: { detail?: string } } })?.response?.data
          ?.detail;
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === 503) {
        setInvoices([]);
        setInvoicesError(null);
      } else {
        setInvoicesError(detail || 'Failed to load invoices');
      }
    } finally {
      setInvoicesLoading(false);
    }
  };

  useEffect(() => {
    loadBilling();
    loadNotifPrefs();
    // Refresh the cached AuthUser on mount so fields that change server-side
    // (e.g. is_verified after clicking an email link in another tab/session,
    // or is_active after admin action) catch up without a full re-login.
    getMe()
      .then((fresh) => {
        updateUser({
          email: fresh.email,
          is_active: fresh.is_active,
          is_verified: fresh.is_verified,
        });
      })
      .catch(() => {
        // Non-critical — banner will still self-heal on next login.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveEmail = async () => {
    if (!emailDraft || emailDraft === user?.email) {
      setEditingEmail(false);
      return;
    }
    setSavingEmail(true);
    setProfileError(null);
    setProfileSuccess(null);
    try {
      const updated = await updateMyProfile({ email: emailDraft });
      updateUser({ email: updated.email });
      setProfileSuccess('Email updated');
      setEditingEmail(false);
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setProfileError(detail || 'Failed to update email');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleOpenPortal = async () => {
    setOpeningPortal(true);
    try {
      const { url } = await createMyBillingPortalSession();
      window.location.href = url;
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setSubscriptionError(detail || 'Failed to open billing portal');
      setOpeningPortal(false);
    }
  };

  const handleSendVerification = async () => {
    setSendingVerify(true);
    try {
      await sendVerificationEmail();
      setVerifySent(true);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setProfileError(detail || 'Failed to send verification email');
    } finally {
      setSendingVerify(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    if (deleteConfirmEmail.trim().toLowerCase() !== user.email.toLowerCase()) {
      setDeleteError('Please type your email to confirm');
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteMyAccount(deleteConfirmEmail.trim());
      logout();
      navigate('/login');
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setDeleteError(detail || 'Failed to delete account');
      setDeleting(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    setCancelError(null);
    try {
      await cancelMySubscription({ immediately: cancelImmediately });
      setShowCancelConfirm(false);
      await loadBilling();
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setCancelError(detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  if (!user) {
    return null;
  }

  const billingConfigured =
    subscription !== null || (subscriptionError === null && !subscriptionLoading);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {user?.needs_subscription && user.role !== 'admin' && (
        <div className="mb-6 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm">
            Your site is paused because this account has no active subscription.
          </span>
          <Link
            to="/register?complete=1"
            className="text-sm font-medium px-3 py-1.5 rounded-full bg-brand-orange text-white hover:bg-brand-orange-dark"
          >
            Complete subscription
          </Link>
        </div>
      )}
      <Link
        to="/"
        className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back to Dashboard
      </Link>

      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Account</h1>

      {/* ============= Profile ============= */}
      <section className="card p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <UserIcon className="w-5 h-5 text-brand-orange" />
          <h2 className="font-semibold text-gray-900 dark:text-white">Profile</h2>
        </div>

        {profileError && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{profileError}</span>
          </div>
        )}
        {profileSuccess && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
            <Check className="w-4 h-4 flex-shrink-0" />
            <span>{profileSuccess}</span>
          </div>
        )}

        {/* Email verification banner */}
        {!user.is_verified && (
          <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-center justify-between gap-3 text-sm text-amber-800 dark:text-amber-200">
            <span className="flex items-center gap-2">
              <Mail className="w-4 h-4 flex-shrink-0" />
              {verifySent
                ? 'Verification email sent — check your inbox.'
                : 'Your email is not verified.'}
            </span>
            {!verifySent && (
              <button
                onClick={handleSendVerification}
                disabled={sendingVerify}
                className="px-3 py-1 text-xs font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-50 flex-shrink-0"
              >
                {sendingVerify ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Resend'}
              </button>
            )}
          </div>
        )}

        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Email
        </label>
        {editingEmail ? (
          <div className="flex gap-2">
            <input
              type="email"
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
            />
            <button
              onClick={handleSaveEmail}
              disabled={savingEmail || !emailDraft}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-orange hover:bg-brand-orange-dark rounded-full disabled:opacity-50"
            >
              {savingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </button>
            <button
              onClick={() => {
                setEmailDraft(user.email);
                setEditingEmail(false);
                setProfileError(null);
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-gray-900 dark:text-white">{user.email}</span>
            <button
              onClick={() => {
                setEditingEmail(true);
                setProfileError(null);
                setProfileSuccess(null);
              }}
              className="flex items-center gap-1 text-sm text-brand-orange hover:opacity-80"
            >
              <Pencil className="w-4 h-4" />
              Edit
            </button>
          </div>
        )}
      </section>

      {/* ============= Notifications ============= */}
      <section className="card p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Bell className="w-5 h-5 text-brand-orange" />
          <h2 className="font-semibold text-gray-900 dark:text-white">Notifications</h2>
        </div>

        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Choose which mobile push notifications you want to receive.
        </p>

        {notifLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : !notifPrefs ? (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {notifError || "Couldn't load notification settings."}
          </div>
        ) : (
          <>
            {notifError && (
              <div className="mb-3 text-sm text-red-600 dark:text-red-400">
                {notifError}
              </div>
            )}
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {notifPrefs.kinds.map((k) => {
                const enabled = notifPrefs.preferences[k.kind] !== false;
                const saving = notifSaving === k.kind;
                return (
                  <li
                    key={k.kind}
                    className="flex items-center justify-between py-3"
                  >
                    <span className="text-sm text-gray-800 dark:text-gray-100">
                      {k.label}
                    </span>
                    <Toggle
                      checked={enabled}
                      disabled={saving}
                      onChange={(next) => onToggleNotif(k.kind, next)}
                      aria-label={k.label}
                    />
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </section>

      {/* ============= Plan ============= */}
      <section className="card p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard className="w-5 h-5 text-brand-orange" />
          <h2 className="font-semibold text-gray-900 dark:text-white">Plan</h2>
        </div>

        {subscriptionLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : subscriptionError ? (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{subscriptionError}</span>
          </div>
        ) : subscription ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">Status</span>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${subscriptionStatusTone(
                  subscription
                )}`}
              >
                {subscriptionStatusLabel(subscription)}
              </span>
            </div>
            {subscription.price && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Price</span>
                <span className="text-sm text-gray-900 dark:text-white">
                  {formatMoney(subscription.price.unit_amount, subscription.price.currency)}
                  {subscription.price.interval ? ` / ${subscription.price.interval}` : ''}
                  {subscription.price.product_name ? ` · ${subscription.price.product_name}` : ''}
                </span>
              </div>
            )}
            {subscription.trial_end && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Trial ends</span>
                <span className="text-sm text-gray-900 dark:text-white">
                  {formatUnixDate(subscription.trial_end)}
                </span>
              </div>
            )}
            {subscription.current_period_end && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {subscription.cancel_at_period_end ? 'Access until' : 'Next billed'}
                </span>
                <span className="text-sm text-gray-900 dark:text-white">
                  {formatUnixDate(subscription.current_period_end)}
                </span>
              </div>
            )}

            <div className="pt-3 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-3">
              <button
                onClick={handleOpenPortal}
                disabled={openingPortal || !billingConfigured}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-orange hover:bg-brand-orange-dark rounded-full disabled:opacity-50"
              >
                {openingPortal ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ExternalLink className="w-4 h-4" />
                )}
                Manage billing & payment method
              </button>
              {subscription.has_subscription && !subscription.cancel_at_period_end && (
                <button
                  onClick={() => {
                    setCancelImmediately(false);
                    setCancelError(null);
                    setShowCancelConfirm(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg"
                >
                  <ShieldAlert className="w-4 h-4" />
                  Suspend / cancel subscription
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Updates to your payment method, downloadable invoices and re-activation are handled
              through the Stripe-hosted billing portal.
            </p>
          </div>
        ) : null}
      </section>

      {/* ============= Invoices ============= */}
      <section className="card p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Receipt className="w-5 h-5 text-brand-orange" />
          <h2 className="font-semibold text-gray-900 dark:text-white">Invoices</h2>
        </div>

        {invoicesLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : invoicesError ? (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{invoicesError}</span>
          </div>
        ) : invoices.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No invoices yet.</p>
        ) : (
          <div className="overflow-x-auto -mx-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className="px-6 py-2 font-medium">Date</th>
                  <th className="px-6 py-2 font-medium">Number</th>
                  <th className="px-6 py-2 font-medium">Amount</th>
                  <th className="px-6 py-2 font-medium">Status</th>
                  <th className="px-6 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {invoices.map((inv) => (
                  <tr key={inv.id}>
                    <td className="px-6 py-2 text-gray-700 dark:text-gray-300">
                      {formatUnixDate(inv.created)}
                    </td>
                    <td className="px-6 py-2 text-gray-700 dark:text-gray-300">
                      {inv.number || '—'}
                    </td>
                    <td className="px-6 py-2 text-gray-900 dark:text-white">
                      {formatMoney(inv.amount_paid || inv.amount_due, inv.currency)}
                    </td>
                    <td className="px-6 py-2 capitalize text-gray-700 dark:text-gray-300">
                      {inv.status}
                    </td>
                    <td className="px-6 py-2 text-right">
                      {(inv.invoice_pdf || inv.hosted_invoice_url) && (
                        <a
                          href={inv.invoice_pdf || inv.hosted_invoice_url || '#'}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-brand-orange hover:opacity-80"
                        >
                          <ExternalLink className="w-3 h-3" />
                          {inv.invoice_pdf ? 'PDF' : 'View'}
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ============= Danger zone ============= */}
      <section className="card border-red-200 dark:border-red-900 p-6 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
          <h2 className="font-semibold text-red-700 dark:text-red-300">Danger zone</h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Deleting your account cancels your subscription and permanently removes your sites and
          their data. This cannot be undone.
        </p>
        <button
          onClick={() => {
            setDeleteConfirmEmail('');
            setDeleteError(null);
            setShowDeleteConfirm(true);
          }}
          className="px-4 py-2 text-sm font-medium text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
        >
          Delete account
        </button>
      </section>

      {/* ============= Delete confirmation modal ============= */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Delete account
              </h2>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                This permanently deletes your account, cancels your subscription and removes all
                of your sites and their data.
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Type <span className="font-mono font-semibold">{user.email}</span> to confirm.
              </p>
              <input
                type="email"
                autoComplete="off"
                value={deleteConfirmEmail}
                onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
              {deleteError && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{deleteError}</span>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete forever'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============= Cancel confirmation modal ============= */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Cancel subscription
              </h2>
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Cancel your SolanIQ subscription? You can reactivate any time from the billing
                portal.
              </p>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={cancelImmediately}
                  onChange={(e) => setCancelImmediately(e.target.checked)}
                  className="rounded"
                />
                Cancel immediately (otherwise you keep access until the end of the current billing
                period)
              </label>
              {cancelError && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{cancelError}</span>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Keep subscription
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-50"
              >
                {cancelling ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : cancelImmediately ? (
                  'Cancel now'
                ) : (
                  'Cancel at period end'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
